'use client';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import { cn } from '@/lib/util/cn';

/**
 * Vim-style leader keymap. Listens for the leader key (`g` for "go to")
 * and, within a short window, matches the second key to a target route.
 *
 *   g t → /trades
 *   g s → /strategies
 *   g n → /broker-accounts   …
 *
 * The pending leader is rendered as a small overlay in the bottom-right
 * so the operator gets visual feedback that they're in leader mode and
 * can keep going. Esc aborts mid-sequence.
 *
 * Focus rule: do nothing if the user is typing in an input / textarea /
 * contenteditable. ⌘K + the palette handle the rest.
 */

const LEADER_KEYS = new Set(['g']);
const PENDING_TTL_MS = 1500;

const KEYMAP: Record<string, string> = {
  'g h': '/control',
  'g t': '/trades',
  'g p': '/pnl',
  'g r': '/risk',
  'g s': '/strategies',
  'g u': '/subscriptions',
  'g n': '/broker-accounts',
  'g b': '/brokers',
  'g d': '/broker-drivers',
  'g c': '/clients',
  'g l': '/watchlists',
  'g y': '/option-chains',
  'g i': '/instruments',
  'g v': '/exchanges',
  'g w': '/data-sources',
  'g o': '/admin/observability',
};

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }
  if (target.isContentEditable) {
    return true;
  }
  return false;
};

export function KeyboardMnemonics() {
  const router = useRouter();
  const [pending, setPending] = useState<string | null>(null);
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  useEffect(() => {
    const clearPending = () => {
      setPending(null);
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const onKey = (e: KeyboardEvent): void => {
      // Skip when a modifier is pressed (⌘K / Ctrl+F etc.).
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }
      if (isTypingTarget(e.target)) {
        return;
      }
      if (e.key === 'Escape') {
        clearPending();
        return;
      }

      const cur = pendingRef.current;
      if (cur === null) {
        if (LEADER_KEYS.has(e.key)) {
          e.preventDefault();
          setPending(e.key);
          if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
          }
          timerRef.current = setTimeout(clearPending, PENDING_TTL_MS);
        }
        return;
      }

      // We have a pending leader. Try to complete the sequence.
      const seq = `${cur} ${e.key}`;
      const target = KEYMAP[seq];
      clearPending();
      if (target !== undefined) {
        e.preventDefault();
        router.push(target as never);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [router]);

  if (pending === null) {
    return null;
  }

  return (
    <div
      className={cn(
        'pointer-events-none fixed bottom-6 left-1/2 z-modal -translate-x-1/2',
        'inline-flex items-center gap-2 rounded-md',
        'border border-border-strong bg-surface-elev px-3 py-2',
        'text-2xs text-fg-muted',
        'shadow-popover backdrop-blur-md',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0',
      )}
      data-state="open"
      role="status"
      aria-live="polite"
    >
      <kbd>{pending}</kbd>
      <span className="text-fg-subtle">…</span>
      <span className="text-fg-subtle">next key navigates</span>
    </div>
  );
}
